// SPDX-License-Identifier: AGPL-3.0-or-later

import type {ScreenshareResolution, StreamingMode} from '@app/features/voice/state/VoiceSettings';
import {
	type ScreenShareCaptureOptions,
	type TrackPublishOptions,
	type VideoEncoding,
	VideoPreset,
} from 'livekit-client';

const DIMENSIONS: Record<
	ScreenshareResolution,
	{
		width: number;
		height: number;
	}
> = {
	low_240p: {width: 426, height: 240},
	low_480p: {width: 854, height: 480},
	medium: {width: 1280, height: 720},
	high: {width: 1920, height: 1080},
	ultra: {width: 2560, height: 1440},
	source: {width: 3840, height: 2160},
};
// Bitrate at 30 FPS; getScreenShareMaxBitrate scales it for the frame rate. In line with what
// Teams (1080p, 1.5-4 Mbps), Google Meet (1.3-2.6 Mbps) and Discord (about 8 Mbps for 1080p60)
// send. Shares used to allow up to 56 Mbps, far more than home uplinks or viewers can carry.
const BASE_BITRATE_BPS: Record<ScreenshareResolution, number> = {
	low_240p: 350000,
	low_480p: 1000000,
	medium: 2500000,
	high: 4000000,
	ultra: 6000000,
	source: 6000000,
};
export const SCREEN_SHARE_MAX_VIDEO_BITRATE_BPS = 100000000;
export const SCREEN_SHARE_GAMING_DEGRADATION_PREFERENCE: NonNullable<TrackPublishOptions['degradationPreference']> =
	'maintain-framerate';
// Outside gaming, a squeezed encoder drops frames and keeps the resolution, so text stays
// readable. maintain-framerate shrank the picture instead, which blurred shared text.
export const SCREEN_SHARE_DEFAULT_DEGRADATION_PREFERENCE: NonNullable<TrackPublishOptions['degradationPreference']> =
	'maintain-resolution';

export function getScreenShareDegradationPreference(
	streamingMode: StreamingMode,
): NonNullable<TrackPublishOptions['degradationPreference']> {
	return streamingMode === 'gaming'
		? SCREEN_SHARE_GAMING_DEGRADATION_PREFERENCE
		: SCREEN_SHARE_DEFAULT_DEGRADATION_PREFERENCE;
}
export const SUPPORTED_SCREEN_SHARE_FRAME_RATES = [15, 30, 60, 90, 120] as const;

export type SupportedScreenShareFrameRate = (typeof SUPPORTED_SCREEN_SHARE_FRAME_RATES)[number];

export function resolveScreenShareFrameRate(frameRate: number): SupportedScreenShareFrameRate {
	if (frameRate >= 120) return 120;
	if (frameRate >= 90) return 90;
	if (frameRate >= 60) return 60;
	if (frameRate >= 30) return 30;
	return 15;
}

export function getScreenShareDimensions(resolution: ScreenshareResolution): {
	width: number;
	height: number;
} {
	return DIMENSIONS[resolution];
}

function getScreenShareMaxBitrate(
	resolution: ScreenshareResolution,
	frameRate: number,
	maxBitrateBps = SCREEN_SHARE_MAX_VIDEO_BITRATE_BPS,
): number {
	const frameRateMultiplier =
		frameRate >= 120 ? 2.5 : frameRate >= 90 ? 2 : frameRate >= 60 ? 1.5 : frameRate >= 30 ? 1 : 0.7;
	return Math.min(Math.round(BASE_BITRATE_BPS[resolution] * frameRateMultiplier), maxBitrateBps);
}

export function getScreenShareEncoding(
	resolution: ScreenshareResolution,
	frameRate: number,
	maxBitrateBps = SCREEN_SHARE_MAX_VIDEO_BITRATE_BPS,
): VideoEncoding {
	return {
		maxBitrate: getScreenShareMaxBitrate(resolution, frameRate, maxBitrateBps),
		maxFramerate: frameRate,
		priority: 'high',
	};
}

// A second, lighter copy of the share for viewers whose connection cannot carry the full one;
// the server picks a layer per viewer (Teams and Google Meet do the same). Outside gaming the
// light layer keeps 720p and drops to 3 FPS at 300 kbps, so text stays readable on a slow line
// and a sharer on a thin uplink still has room for the full layer (at 600 kbps / 5 FPS the full
// layer kept switching off on a 2 Mbit/s uplink). Gaming keeps motion at 30 FPS instead.
const LIGHT_LAYER_BITRATE_BPS: Record<720 | 360 | 240, {detail: number; motion: number}> = {
	720: {detail: 300000, motion: 1500000},
	360: {detail: 250000, motion: 500000},
	240: {detail: 150000, motion: 250000},
};
const LIGHT_LAYER_DETAIL_FRAME_RATE = 3;
const LIGHT_LAYER_MOTION_MAX_FRAME_RATE = 30;

export function getScreenShareSimulcastLayers(
	resolution: ScreenshareResolution,
	frameRate: number,
	streamingMode: StreamingMode,
): Array<VideoPreset> {
	const top = DIMENSIONS[resolution];
	if (top.height <= 360) return [];
	const lightHeight = top.height > 720 ? 720 : top.height === 720 ? 360 : 240;
	const lightWidth = Math.round((top.width * lightHeight) / top.height);
	const motion = streamingMode === 'gaming';
	const lightFrameRate = motion
		? Math.min(resolveScreenShareFrameRate(frameRate), LIGHT_LAYER_MOTION_MAX_FRAME_RATE)
		: LIGHT_LAYER_DETAIL_FRAME_RATE;
	const bitrate = LIGHT_LAYER_BITRATE_BPS[lightHeight][motion ? 'motion' : 'detail'];
	return [new VideoPreset(lightWidth, lightHeight, bitrate, lightFrameRate, 'high')];
}

export const STREAMING_MODE_PRESETS: Record<
	Exclude<StreamingMode, 'custom'>,
	{
		resolution: ScreenshareResolution;
		frameRate: SupportedScreenShareFrameRate;
	}
> = {
	gaming: {resolution: 'ultra', frameRate: 60},
	screenshare: {resolution: 'high', frameRate: 15},
};
// Only the Gaming preset goes above 1080p. A 4K share is too much for many viewers' connections
// and decoders, and it bought no readable detail over 1080p.
const MAX_CUSTOM_SCREEN_SHARE_RESOLUTION: ScreenshareResolution = 'high';
const RESOLUTIONS_ABOVE_CUSTOM_CAP: ReadonlyArray<ScreenshareResolution> = ['ultra', 'source'];

export function clampCustomScreenShareResolution(resolution: ScreenshareResolution): ScreenshareResolution {
	return RESOLUTIONS_ABOVE_CUSTOM_CAP.includes(resolution) ? MAX_CUSTOM_SCREEN_SHARE_RESOLUTION : resolution;
}
const FREE_STREAMING_MODE_PRESETS: Record<
	Exclude<StreamingMode, 'custom'>,
	{
		resolution: ScreenshareResolution;
		frameRate: SupportedScreenShareFrameRate;
	}
> = {
	gaming: {resolution: 'medium', frameRate: 30},
	screenshare: {resolution: 'medium', frameRate: 15},
};

export interface BuiltScreenShareOptions {
	captureOptions: ScreenShareCaptureOptions;
	publishOptions: TrackPublishOptions;
}

export interface ScreenShareBuildConfig {
	resolution: ScreenshareResolution;
	frameRate: number;
	includeAudio: boolean;
	streamingMode?: StreamingMode;
	contentHint?: ScreenShareCaptureOptions['contentHint'];
	maxBitrateBps?: number;
	sourceDimensions?: {
		width: number;
		height: number;
	};
	preferredDisplaySurface?: 'window' | 'monitor';
}

type ScreenShareVideoOptions = NonNullable<Exclude<ScreenShareCaptureOptions['video'], true>> & {
	cursor?: 'always' | 'motion' | 'never';
};

function resolveScreenShareCursorCapture(
	preferredDisplaySurface?: ScreenShareBuildConfig['preferredDisplaySurface'],
): 'always' | 'never' {
	return preferredDisplaySurface === 'window' ? 'never' : 'always';
}

export function resolveEffectiveScreenShareDimensions(
	resolution: ScreenshareResolution,
	sourceDimensions?: {
		width: number;
		height: number;
	},
): {
	width: number;
	height: number;
} {
	const preset = getScreenShareDimensions(resolution);
	if (resolution !== 'source' || !sourceDimensions) return preset;
	if (sourceDimensions.width <= 0 || sourceDimensions.height <= 0) return preset;
	return {
		width: Math.min(preset.width, sourceDimensions.width),
		height: Math.min(preset.height, sourceDimensions.height),
	};
}

export function buildScreenShareOptions(config: ScreenShareBuildConfig): BuiltScreenShareOptions;
export function buildScreenShareOptions(resolution: ScreenshareResolution, frameRate: number): BuiltScreenShareOptions;
export function buildScreenShareOptions(
	configOrResolution: ScreenShareBuildConfig | ScreenshareResolution,
	maybeFrameRate?: number,
): BuiltScreenShareOptions {
	const config: ScreenShareBuildConfig =
		typeof configOrResolution === 'object'
			? configOrResolution
			: {resolution: configOrResolution, frameRate: maybeFrameRate ?? 30, includeAudio: true};
	const {width, height} = resolveEffectiveScreenShareDimensions(config.resolution, config.sourceDimensions);
	const resolvedFrameRate = resolveScreenShareFrameRate(config.frameRate);
	const video: ScreenShareVideoOptions = {
		cursor: resolveScreenShareCursorCapture(config.preferredDisplaySurface),
		...(config.preferredDisplaySurface ? {displaySurface: config.preferredDisplaySurface} : {}),
	};
	const streamingMode = config.streamingMode ?? 'screenshare';
	const degradationPreference = getScreenShareDegradationPreference(streamingMode);
	const simulcastLayers = getScreenShareSimulcastLayers(config.resolution, resolvedFrameRate, streamingMode);
	return {
		captureOptions: {
			audio: config.includeAudio,
			...(config.contentHint ? {contentHint: config.contentHint} : {}),
			...(config.includeAudio ? {restrictOwnAudio: true} : {}),
			selfBrowserSurface: 'include',
			monitorTypeSurfaces: config.preferredDisplaySurface === 'window' ? 'exclude' : 'include',
			systemAudio: 'exclude',
			windowAudio: config.includeAudio ? 'window' : 'exclude',
			resolution: {width, height, frameRate: resolvedFrameRate},
			video,
		},
		publishOptions: {
			degradationPreference,
			screenShareEncoding: getScreenShareEncoding(config.resolution, resolvedFrameRate, config.maxBitrateBps),
			simulcast: simulcastLayers.length > 0,
			screenShareSimulcastLayers: simulcastLayers,
		},
	};
}

const FREE_TIER_FALLBACK_RESOLUTION: ScreenshareResolution = 'medium';
const FREE_TIER_RESOLUTIONS: ReadonlyArray<ScreenshareResolution> = ['low_240p', 'low_480p', 'medium'];
const FREE_TIER_MAX_FRAME_RATE: SupportedScreenShareFrameRate = 30;

function isFreeTierResolution(resolution: ScreenshareResolution): boolean {
	return FREE_TIER_RESOLUTIONS.includes(resolution);
}

function clampToFreeTier(
	resolution: ScreenshareResolution,
	frameRate: SupportedScreenShareFrameRate,
): {
	resolution: ScreenshareResolution;
	frameRate: SupportedScreenShareFrameRate;
} {
	const cappedResolution: ScreenshareResolution = isFreeTierResolution(resolution)
		? resolution
		: FREE_TIER_FALLBACK_RESOLUTION;
	const cappedFrameRate: SupportedScreenShareFrameRate =
		frameRate > FREE_TIER_MAX_FRAME_RATE ? FREE_TIER_MAX_FRAME_RATE : frameRate;
	return {resolution: cappedResolution, frameRate: cappedFrameRate};
}

export function resolveStreamingModeSettings(
	mode: StreamingMode,
	customResolution: ScreenshareResolution,
	customFrameRate: number,
	hasHigherQuality: boolean = true,
): {
	resolution: ScreenshareResolution;
	frameRate: SupportedScreenShareFrameRate;
} {
	const resolved =
		mode === 'custom'
			? {
					resolution: clampCustomScreenShareResolution(customResolution),
					frameRate: resolveScreenShareFrameRate(customFrameRate),
				}
			: (hasHigherQuality ? STREAMING_MODE_PRESETS : FREE_STREAMING_MODE_PRESETS)[mode];
	if (hasHigherQuality) {
		return resolved;
	}
	return clampToFreeTier(resolved.resolution, resolved.frameRate);
}

export type ScreenShareContext = 'display' | 'device';

export function normaliseStreamingModeForContext(mode: StreamingMode, context: ScreenShareContext): StreamingMode {
	if (context === 'device' && mode === 'screenshare') {
		return 'gaming';
	}
	return mode;
}

export function normaliseResolutionForContext(
	resolution: ScreenshareResolution,
	context: ScreenShareContext,
	hasHigherQuality: boolean,
): ScreenshareResolution {
	if (!hasHigherQuality && !isFreeTierResolution(resolution)) {
		return FREE_TIER_FALLBACK_RESOLUTION;
	}
	if (context === 'device' && resolution === 'source') {
		return hasHigherQuality ? 'ultra' : FREE_TIER_FALLBACK_RESOLUTION;
	}
	return resolution;
}

/**
 * Applies a change to the full-quality encoding only. LiveKit orders simulcast encodings from
 * the lightest to the full one, so the last entry is the full share; the light layer keeps the
 * bitrate, frame rate and scale it was published with.
 */
export function mapFullScreenShareEncoding(
	encodings: ReadonlyArray<RTCRtpEncodingParameters>,
	update: (encoding: RTCRtpEncodingParameters) => RTCRtpEncodingParameters,
): Array<RTCRtpEncodingParameters> {
	const lastIndex = encodings.length - 1;
	return encodings.map((encoding, index) => (index === lastIndex ? update(encoding) : {...encoding}));
}
